import React, { useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const COMPOSITION_COLORS = {
  fi_pos: '#2F72A1',
  fi_pre_ipca: '#4497C5',
  fi_international: '#55B3E0',
  multiasset: '#6BC5DB',
  equities_br: '#82D8D5',
  equities_international: '#95DEDD',
  fii: '#A9E4E5',
  alternatives: '#CFEFF1',
  // offshore
  off_fixed_income_long_term: '#2F72A1',
  off_fixed_income_short_term: '#55B3E0',
  off_equities: '#6BC5DB',
  off_reits: '#82D8D5',
  off_commodities: '#A9E4E5',
  account_balance_percentage: '#CFEFF1',
  // unclassified
  unclassified: '#E6FAFB',
};

const DEFAULT_LABELS = {
  fi_pos: 'Renda Fixa - Pós',
  fi_pre_ipca: 'Renda Fixa - Pré/IPCA+',
  fi_international: 'Renda Fixa - Internacional',
  multiasset: 'Multimercado',
  equities_br: 'Ações - Brasil',
  equities_international: 'Ações - Internacional',
  fii: 'Fundos Imobiliários',
  alternatives: 'Alternativos',
  unclassified: 'Não classificado',
};

const COMPOSITION_ORDER = [
  'fi_pos',
  'fi_pre_ipca',
  'fi_international',
  'multiasset',
  'equities_br',
  'equities_international',
  'fii',
  'alternatives',
  'off_fixed_income_long_term',
  'off_fixed_income_short_term',
  'off_equities',
  'off_reits',
  'off_commodities',
  'account_balance_percentage',
  'unclassified',
];

function formatPercentage(value) {
  const number = Number(value) || 0;

  return `${number.toLocaleString('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
}

function getItemValue(itemsByKey, key) {
  const item = itemsByKey[key];

  return Number(item?.value ?? item?.percentage ?? 0) || 0;
}

function buildItemsByKey(items = []) {
  return items.reduce((acc, item) => {
    if (!item?.key) return acc;

    acc[item.key] = item;
    return acc;
  }, {});
}

function buildComparisonRows(currentItems = [], suggestedItems = []) {
  const currentByKey = buildItemsByKey(currentItems);
  const suggestedByKey = buildItemsByKey(suggestedItems);

  const existingKeys = new Set([
    ...currentItems.map((item) => item.key).filter(Boolean),
    ...suggestedItems.map((item) => item.key).filter(Boolean),
  ]);

  const orderedKnownKeys = COMPOSITION_ORDER.filter((key) => existingKeys.has(key));
  const unknownKeys = [...existingKeys].filter((key) => !COMPOSITION_ORDER.includes(key));
  const finalKeys = [...orderedKnownKeys, ...unknownKeys];

  return finalKeys.map((key) => {
    const currentItem = currentByKey[key];
    const suggestedItem = suggestedByKey[key];

    return {
      key,
      label: DEFAULT_LABELS[key] || currentItem?.label || suggestedItem?.label || key,
      color: COMPOSITION_COLORS[key] || '#CFEFF1',
      currentValue: getItemValue(currentByKey, key),
      suggestedValue: getItemValue(suggestedByKey, key),
    };
  });
}

function buildDonutData(rows, side) {
  return rows
    .map((row) => ({
      key: row.key,
      label: row.label,
      color: row.color,
      value: side === 'current' ? row.currentValue : row.suggestedValue,
    }))
    .filter((item) => item.value > 0);
}

function CompositionTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;

  const item = payload[0]?.payload;

  return (
    <div
      style={{
        backgroundColor: '#FFFFFF',
        border: '1px solid #D8F6F5',
        borderRadius: 10,
        padding: '8px 10px',
        boxShadow: '0 8px 20px rgba(20, 49, 69, 0.12)',
        color: '#143145',
        fontSize: 12,
      }}
    >
      <div style={{ fontWeight: 600 }}>{item.label}</div>
      <div>{formatPercentage(item.value)}</div>
    </div>
  );
}

function CompositionDonut({ title, data }) {
  return (
    <div className="w-full flex flex-col items-center">
      <div
        className="relative w-full"
        style={{
          maxWidth: 230,
          aspectRatio: '1 / 1',
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Tooltip content={<CompositionTooltip />} wrapperStyle={{ zIndex: 9999 }} />

            <Pie
              data={data}
              dataKey="value"
              nameKey="label"
              cx="50%"
              cy="50%"
              innerRadius="45%"
              outerRadius="82%"
              startAngle={90}
              endAngle={-270}
              paddingAngle={0}
              stroke="none"
              isAnimationActive={false}
            >
              {data.map((item) => (
                <Cell key={item.key} fill={item.color} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>

        <div
          className="absolute left-1/2 top-1/2 flex items-center justify-center text-center"
          style={{
            width: '46%',
            aspectRatio: '1 / 1',
            transform: 'translate(-50%, -50%)',
            borderRadius: '50%',
            backgroundColor: '#FFFFFF',
            color: '#143145',
            fontSize: 13,
            fontWeight: 500,
            pointerEvents: 'none',
          }}
        >
          {title}
        </div>
      </div>
    </div>
  );
}

function DesktopCompositionLegend({ rows }) {
  return (
    <div className="hidden sm:flex w-full flex-col gap-2 mt-3">
      {rows.map((row) => (
        <div key={row.key} className="grid grid-cols-12 items-center min-h-10 gap-3">
          <div className="col-span-3 flex items-center min-w-0">
            <div
              style={{
                width: 44,
                height: 26,
                borderRadius: 7,
                backgroundColor: row.color,
                flexShrink: 0,
              }}
            />

            <strong
              className="ml-3 text-blueDarker whitespace-nowrap"
              style={{
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {formatPercentage(row.currentValue)}
            </strong>
          </div>

          <div
            className="col-span-6 text-center text-blueDarker"
            style={{
              fontSize: 13,
              fontWeight: 400,
              lineHeight: '18px',
            }}
          >
            {row.label}
          </div>

          <div className="col-span-3 flex items-center justify-end min-w-0">
            <strong
              className="mr-3 text-blueDarker whitespace-nowrap"
              style={{
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {formatPercentage(row.suggestedValue)}
            </strong>

            <div
              style={{
                width: 44,
                height: 26,
                borderRadius: 7,
                backgroundColor: row.color,
                flexShrink: 0,
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function MobileCompositionLegend({ rows }) {
  return (
    <div className="sm:hidden flex flex-col gap-2 mt-3">
      {rows.map((row) => (
        <div
          key={row.key}
          className="bg-blueLight px-3 py-3"
          style={{
            borderRadius: 12,
          }}
        >
          <div className="flex items-center gap-2 mb-3">
            <div
              style={{
                width: 14,
                height: 14,
                borderRadius: 5,
                backgroundColor: row.color,
                flexShrink: 0,
              }}
            />

            <span
              className="text-blueDarker"
              style={{
                fontSize: 12,
                fontWeight: 600,
                lineHeight: '16px',
              }}
            >
              {row.label}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="bg-white px-3 py-2" style={{ borderRadius: 10 }}>
              <div className="text-blueSteel text-xs">Atual</div>
              <strong className="text-blueDarker text-sm">{formatPercentage(row.currentValue)}</strong>
            </div>

            <div className="bg-white px-3 py-2" style={{ borderRadius: 10 }}>
              <div className="text-blueSteel text-xs">Sugerida</div>
              <strong className="text-blueDarker text-sm">{formatPercentage(row.suggestedValue)}</strong>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function CompositionLegend({ rows }) {
  return (
    <>
      <DesktopCompositionLegend rows={rows} />
      <MobileCompositionLegend rows={rows} />
    </>
  );
}

export default function InvestmentAllocationComparisonDonuts({
  currentComposition,
  suggestedComposition,
  currentTitle = 'Atual',
  suggestedTitle = 'Sugerida',
}) {
  const rows = useMemo(
    () => buildComparisonRows(currentComposition?.items || [], suggestedComposition?.items || []),
    [currentComposition, suggestedComposition]
  );

  const currentDonutData = useMemo(() => buildDonutData(rows, 'current'), [rows]);
  const suggestedDonutData = useMemo(() => buildDonutData(rows, 'suggested'), [rows]);

  const hasData = currentDonutData.length > 0 || suggestedDonutData.length > 0;

  if (!hasData) {
    return (
      <div
        className="w-full flex items-center justify-center text-blueDarker"
        style={{
          minHeight: 220,
          fontSize: 14,
        }}
      >
        Nenhuma composição disponível.
      </div>
    );
  }

  return (
    <div className="w-full bg-white">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8 lg:gap-10 items-start">
        <CompositionDonut title={currentTitle} data={currentDonutData} />
        <CompositionDonut title={suggestedTitle} data={suggestedDonutData} />
      </div>

      <CompositionLegend rows={rows} />
    </div>
  );
}
